import React from "react";
import dayjs from "dayjs";

import { Money } from "../../../../components";
import { Button, Icon, IconButton, Menu } from "../../../../components/ui";
import { Visible } from "../../../../core/components";
import { conditionLabels } from "../../../../product/constants";
import useQuoteActions from "../../QuoteWorkspace/useQuoteActions";
import { useQuoteProductPricing } from "../../../hooks/quoteProductPricing";
import StatusIcon from "./StatusIcon";

// The quote's next step as a Button, the rest behind a Menu — the workspace's action bar at row
// size, from the same useQuoteActions, so the row and the editor offer the same calls. Actions that
// need an inline tray (reviewer, sell send type, change listing) open the quote, where the tray is.
const QuoteRowActions = ({ quote, onQuoteSaved, openQuote }) => {
  const { primary, secondary, note } = useQuoteActions(quote, onQuoteSaved);
  const run = (action) => (action.panel ? openQuote() : action.onClick());

  return (
    <div className="quotes-list__actions">
      {primary ? (
        <Button
          variant="secondary"
          size="sm"
          loading={primary.loading}
          leadingIcon={<Icon name={primary.icon} size="sm" />}
          onClick={() => run(primary)}
        >
          {primary.label}
        </Button>
      ) : (
        note && <span className="quotes-list__note">{note}</span>
      )}
      {secondary.length > 0 && (
        <Menu
          label="Quote actions"
          items={secondary.map((a) => ({
            label: a.label,
            icon: <Icon name={a.icon} size="sm" />,
            disabled: a.disabled || a.loading,
            onSelect: () => run(a),
          }))}
          trigger={(
            <IconButton size="sm" label="More quote actions">
              <Icon name="MoreHorizontal" size="sm" />
            </IconButton>
          )}
        />
      )}
    </div>
  );
};

const APPROVED_STATUSES = ["approved", "quote_sent", "accepted_by_client"];

const normalizeStatus = (status) =>
  typeof status === "string" ? status.toLowerCase() : status;

const sameRequestId = (left, right) => {
  if (left == null || right == null) return false;
  return String(left) === String(right);
};

const quoteBelongsToRequest = (quote, requestId, { liveCalculation = false } = {}) => {
  if (liveCalculation) return true;
  if (requestId == null) return false;
  // List is already filtered by request_id — missing on a row still
  // means "this request's quote" from the API payload.
  const quoteRequestId = quote.request_id ?? requestId;
  return sameRequestId(quoteRequestId, requestId);
};

const QuoteItem = ({
  item,
  onSelectClick,
  chooseQuote,
  chosenQuote,
  onlyApproved,
  liveCalculation = false,
  requestId,
  // SELECT ORDER/SELL QUOTE only: the quote's own commands, on its own row (QuoteRowActions
  // above), so the options offered are the ones that quote's status actually allows — approve,
  // send, mark accepted, create/link a listing — without having to open the quote to reach them.
  rowActions = false,
  onRowQuoteSaved,
}) => {
  const status = normalizeStatus(item.status);
  const isApproved = APPROVED_STATUSES.includes(status);
  const belongsToRequest = quoteBelongsToRequest(item, requestId, {
    liveCalculation,
  });
  const isSelectable =
    belongsToRequest && (!onlyApproved || isApproved);
  // Supplier Name / Buying Price come from the quote's PRODUCT PRICING row, so
  // stores without that dropdown don't show the two columns at all (the
  // headers in QuoteListView follow the same flag).
  const hasProductPricing = useQuoteProductPricing();

  return (
    <tr className="quotes-list__row" onClick={() => onSelectClick(item)}>
      <Visible when={chooseQuote}>
        <td className="quotes-list__cell quotes-list__cell--select" data-label="">
          <input
            type="checkbox"
            checked={chosenQuote?.id === item.id}
            disabled={!isSelectable}
            className="choose-quote-input"
            onChange={() => chooseQuote(item)}
            onClick={(e) => e.stopPropagation()}
          />
        </td>
      </Visible>

      <td className="quotes-list__cell quotes-list__cell--status" data-label="Status">
        <StatusIcon item={item} requestId={requestId} />
      </td>

      <Visible when={rowActions}>
        {/* Second column, beside the status it acts on — as the LAST of ten columns it sat
            off the right edge of the section and nobody could reach it (Alex, 2026-09-05).
            The row itself opens the quote editor, so the cell swallows its own clicks:
            otherwise picking an action would open the editor behind the dropdown. */}
        <td
          className="quotes-list__cell quotes-list__cell--actions"
          data-label="Action"
          onClick={(e) => e.stopPropagation()}
        >
          <QuoteRowActions
            quote={item}
            onQuoteSaved={onRowQuoteSaved}
            openQuote={() => onSelectClick(item)}
          />
        </td>
      </Visible>

      <td className="quotes-list__cell" data-label="Created">
        {dayjs(item.request_date).format("DD/MM/YYYY")}
      </td>

      <td className="quotes-list__cell" data-label="Admin Name">
        {item.user?.first_name} {item.user?.last_name}
      </td>

      <td className="quotes-list__cell" data-label="Quoted Price">
        <Money amount={item.price} currency={item.currency} className="money" />
      </td>

      <Visible when={item.type === "sell"}>
        <td className="quotes-list__cell" data-label="Buyer">
          {item.buyer}
        </td>
        <td className="quotes-list__cell" data-label="Consignment">
          {item.min_price && (
            <>
              <Money
                amount={item.min_price}
                currency={item.currency}
                className="money"
              />
              &nbsp;-&nbsp;
              <Money
                amount={item.max_price}
                currency={item.currency}
                className="money"
              />
            </>
          )}
        </td>
      </Visible>

      <Visible when={item.type === "order" && hasProductPricing}>
        <td className="quotes-list__cell" data-label="Supplier Name">
          {item.product_price?.supplier_id
            ? item.product_price?.supplier_name
            : null}
        </td>

        <td className="quotes-list__cell" data-label="Buying Price">
          <Money
            amount={item.product_price?.price}
            currency={item.product_price?.currency}
            className="money"
          />
        </td>
      </Visible>

      <td className="quotes-list__cell" data-label="Year">
        {item.year || item.product_price?.year_month?.split(" ")[0]}
      </td>

      <td className="quotes-list__cell" data-label="Condition">
        {conditionLabels[item.condition || item.product_price?.condition]}
      </td>

    </tr>
  );
};

export default QuoteItem;
